import { useEffect, useRef, useState } from "react"

interface AnimatedTitleProps {
  title: string
  className?: string
  animationSpeed?: number // ms per character
  onAnimationComplete?: () => void
}

export function AnimatedTitle({
  title,
  className = "",
  animationSpeed = 30,
  onAnimationComplete,
}: AnimatedTitleProps) {
  const [displayedTitle, setDisplayedTitle] = useState("")
  const [isAnimating, setIsAnimating] = useState(false)
  const previousTitleRef = useRef(title)
  const animationTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  useEffect(() => {
    // Check if title has changed
    if (title !== previousTitleRef.current) {
      // Clear any existing animation
      if (animationTimeoutRef.current !== undefined) {
        clearTimeout(animationTimeoutRef.current)
      }

      // Start typing animation
      setIsAnimating(true)
      setDisplayedTitle("")

      let currentIndex = 0

      const typeNextCharacter = () => {
        if (currentIndex < title.length) {
          setDisplayedTitle(title.slice(0, currentIndex + 1))
          currentIndex++
          animationTimeoutRef.current = setTimeout(typeNextCharacter, animationSpeed)
        } else {
          // Animation complete
          setIsAnimating(false)
          previousTitleRef.current = title
          onAnimationComplete?.()
        }
      }

      typeNextCharacter()
    } else {
      // No change, just display the title
      setDisplayedTitle(title)
    }

    return () => {
      if (animationTimeoutRef.current !== undefined) {
        clearTimeout(animationTimeoutRef.current)
      }
    }
  }, [title, animationSpeed, onAnimationComplete])

  return (
    <span className={className}>
      {displayedTitle}
      {isAnimating && <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-current" />}
    </span>
  )
}
